import type { SheetStock } from "@proof/core";
import { Control, Toggle } from "./components";
import { useEffect, useState } from "react";
export function SheetStockEditor({
  value,
  onChange,
}: {
  value?: SheetStock;
  onChange: (value: SheetStock) => void;
}) {
  const [currencyText, setCurrencyText] = useState(value?.currency || "");
  useEffect(() => setCurrencyText(value?.currency || ""), [value?.currency]);
  const change = (patch: Partial<SheetStock>) =>
    onChange({ material: "Paper", ...value, ...patch });
  return (
    <fieldset
      className="sheet-stock-editor"
      style={{ border: 0, padding: 0, minWidth: 0 }}
    >
      <h4>Paper and print stock</h4>
      <div className="control-grid">
        {(
          [
            ["material", "Material"],
            ["color", "Stock color"],
            ["coating", "Coating"],
            ["finish", "Finish / finishing"],
            ["press", "Press / equipment"],
            ["supplier", "Supplier"],
            ["sku", "Stock SKU"],
          ] as const
        ).map(([key, label]) => (
          <Control key={key} label={label}>
            <input
              value={value?.[key] || ""}
              maxLength={
                key === "sku" || key === "color" || key === "coating"
                  ? 100
                  : 200
              }
              onChange={(e) => change({ [key]: e.target.value })}
            />
          </Control>
        ))}
        {(
          [
            ["weightGsm", "Paper weight (GSM)", 5000],
            ["caliperMm", "Caliper (mm)", 100],
            ["costPerSheet", "Cost per physical sheet", 1000000],
            ["recycledPercent", "Recycled content (%)", 100],
          ] as const
        ).map(([key, label, max]) => (
          <Control key={key} label={label}>
            <input
              type="number"
              min={0}
              max={max}
              step="any"
              value={value?.[key] ?? ""}
              onChange={(e) =>
                change({
                  [key]:
                    e.target.value === "" ? undefined : Number(e.target.value),
                })
              }
            />
          </Control>
        ))}
        <Control label="Cost currency">
          <input
            maxLength={3}
            placeholder="USD"
            value={currencyText}
            onChange={(e) => {
              const currency = e.target.value.toUpperCase();
              setCurrencyText(currency);
              if (!currency || /^[A-Z]{3}$/.test(currency))
                change({ currency: currency || undefined });
            }}
          />
        </Control>
        <Control label="Grain direction">
          <select
            value={value?.grain || "unspecified"}
            onChange={(e) =>
              change({ grain: e.target.value as SheetStock["grain"] })
            }
          >
            <option value="unspecified">Unspecified</option>
            <option value="short">Short grain</option>
            <option value="long">Long grain</option>
          </select>
        </Control>
        <Control label="Printed sides">
          <select
            value={value?.sides || "simplex"}
            onChange={(e) =>
              change({ sides: e.target.value as SheetStock["sides"] })
            }
          >
            <option value="simplex">One side / simplex</option>
            <option value="duplex">Two sides / duplex</option>
          </select>
        </Control>
        <Control label="Printing process">
          <select
            value={value?.process || "digital"}
            onChange={(e) =>
              change({ process: e.target.value as SheetStock["process"] })
            }
          >
            {["digital", "offset", "inkjet", "screen", "other"].map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </Control>
      </div>
      <Toggle
        label="Certified sustainable stock"
        value={!!value?.certifiedSustainable}
        onChange={(certifiedSustainable) => change({ certifiedSustainable })}
      />
      <Control label="Paper / print notes">
        <textarea
          rows={3}
          maxLength={2000}
          value={value?.notes || ""}
          onChange={(e) => change({ notes: e.target.value })}
        />
      </Control>
      <small className="muted">
        Material estimate uses physical sheets and the configured stock cost.
        Press time, tax, spoilage and finishing charges are not included.
      </small>
    </fieldset>
  );
}
